import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  PageSection,
  Spinner,
  TextArea,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet, apiPost } from "../../app/api";
import { compactDateTime, formatQuantity } from "../../display";

type WasteLot = {
  lot_id: number;
  location_id: number;
  location_code?: string;
  location_name?: string;
  product_id: number;
  sku?: string;
  product_name?: string;
  product_type?: string;
  uom?: string;
  lot_code?: string;
  expires_at?: string;
  qty_on_hand?: number;
};

type WasteEvent = {
  id?: number;
  exception_number?: string;
  exception_type?: string;
  product_name?: string;
  sku?: string;
  location_code?: string;
  location_name?: string;
  quantity?: number;
  status?: string;
  reason?: string;
  created_by_name?: string;
  created_at?: string;
};

type WastePostResult = {
  ok: boolean;
  exception_id?: number;
  exception_number?: string;
  status?: string;
};

const WASTE_TYPES = [
  { value: "spoilage", label: "Spoilage" },
  { value: "wastage", label: "Wastage" },
  { value: "breakage", label: "Breakage" },
  { value: "transit_loss", label: "Transit loss" },
  { value: "store_use", label: "Store use" },
  { value: "production_waste", label: "Production waste" },
  { value: "transfer_discrepancy", label: "Transfer discrepancy" }
];

function lotLabel(lot: WasteLot): string {
  const location = [lot.location_code, lot.location_name].filter(Boolean).join(" - ");
  const item = [lot.sku, lot.product_name].filter(Boolean).join(" - ");
  const qty = `${formatQuantity(lot.qty_on_hand)} ${lot.uom || "units"}`;
  const lotCode = lot.lot_code ? `Lot ${lot.lot_code}` : "No lot code";
  return `${location} | ${item} | ${qty} | ${lotCode}`;
}

function eventTypeLabel(value: string | undefined): string {
  return WASTE_TYPES.find(type => type.value === value)?.label || String(value || "").replace(/_/g, " ");
}

function cleanNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function WasteLossWorkspace({ csrfToken }: { csrfToken: string }) {
  const [lots, setLots] = useState<WasteLot[]>([]);
  const [events, setEvents] = useState<WasteEvent[]>([]);
  const [lotId, setLotId] = useState("");
  const [exceptionType, setExceptionType] = useState("wastage");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger" | "warning"; title: string } | null>(null);

  const selectedLot = useMemo(
    () => lots.find(lot => String(lot.lot_id) === String(lotId)),
    [lotId, lots]
  );

  const filteredLots = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return lots;
    return lots.filter(lot => lotLabel(lot).toLowerCase().includes(query));
  }, [lots, search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [lotPayload, eventPayload] = await Promise.all([
        apiGet<{ lots: WasteLot[] }>("/api/manual-adjustments/lots"),
        apiGet<{ exceptions: WasteEvent[] }>("/api/inventory-exceptions")
      ]);
      const nextLots = lotPayload.lots || [];
      setLots(nextLots);
      setEvents(eventPayload.exceptions || []);
      setLotId(current => nextLots.some(lot => String(lot.lot_id) === current) ? current : String(nextLots[0]?.lot_id || ""));
    } catch (err) {
      setLots([]);
      setEvents([]);
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Waste and loss records could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const submitWaste = async () => {
    const parsedQuantity = cleanNumber(quantity);
    if (!selectedLot) {
      setMessage({ variant: "warning", title: "Choose the inventory item and lot first." });
      return;
    }
    if (parsedQuantity === null) {
      setMessage({ variant: "warning", title: "Enter a quantity greater than zero." });
      return;
    }
    if (parsedQuantity > Number(selectedLot.qty_on_hand || 0)) {
      setMessage({ variant: "warning", title: "Quantity cannot be higher than the lot quantity on hand." });
      return;
    }
    if (!reason.trim()) {
      setMessage({ variant: "warning", title: "Enter a short reason before saving." });
      return;
    }
    setPosting(true);
    setMessage(null);
    try {
      const result = await apiPost<WastePostResult>("/api/inventory-exceptions", {
        location_id: selectedLot.location_id,
        product_id: selectedLot.product_id,
        lot_id: selectedLot.lot_id,
        exception_type: exceptionType,
        quantity: parsedQuantity,
        reason: reason.trim(),
        evidence_ref: evidenceRef.trim()
      }, csrfToken);
      setQuantity("");
      setReason("");
      setEvidenceRef("");
      setMessage({ variant: "success", title: `${result.exception_number || "Waste / loss"} recorded.` });
      await loadData();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Waste or loss could not be saved." });
    } finally {
      setPosting(false);
    }
  };

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-waste-loss-view" className="pf-toj-waste-loss">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>Waste / Loss Reporting</h1>
            <p>Record spoilage, breakage, loss, or correction against the exact stock lot.</p>
          </FlexItem>
          {loading && (
            <FlexItem>
              <Spinner size="md" aria-label="Loading waste and loss reporting" />
            </FlexItem>
          )}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <div className="pf-toj-waste-grid">
          <Card className="pf-toj-work-card pf-toj-primary-work-card">
            <CardBody>
              <h2>Record waste or loss</h2>
              <Form className="pf-toj-form">
                <FormGroup label="Find stock item" fieldId="pf-waste-search">
                  <TextInput
                    id="pf-waste-search"
                    value={search}
                    placeholder="Search item, SKU, location, or lot"
                    onChange={(_event, value) => setSearch(String(value))}
                    aria-label="Search waste or loss lots"
                  />
                </FormGroup>
                <FormGroup label="Stock lot" fieldId="pf-waste-lot">
                  <FormSelect
                    id="pf-waste-lot"
                    value={lotId}
                    onChange={(_event, value) => setLotId(String(value))}
                    aria-label="Waste or loss stock lot"
                  >
                    {!filteredLots.length && <FormSelectOption value="" label="No stock lots available" />}
                    {filteredLots.map(lot => (
                      <FormSelectOption key={lot.lot_id} value={String(lot.lot_id)} label={lotLabel(lot)} />
                    ))}
                  </FormSelect>
                </FormGroup>
                <div className="pf-toj-field-row">
                  <FormGroup label="Reason type" fieldId="pf-waste-type">
                    <FormSelect
                      id="pf-waste-type"
                      value={exceptionType}
                      onChange={(_event, value) => setExceptionType(String(value))}
                      aria-label="Waste or loss reason type"
                    >
                      {WASTE_TYPES.map(type => (
                        <FormSelectOption key={type.value} value={type.value} label={type.label} />
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup label="Quantity" fieldId="pf-waste-quantity">
                    <TextInput
                      id="pf-waste-quantity"
                      type="number"
                      min={0}
                      step="0.01"
                      value={quantity}
                      onChange={(_event, value) => setQuantity(String(value))}
                      aria-label="Waste or loss quantity"
                    />
                  </FormGroup>
                  <FormGroup label="Reference" fieldId="pf-waste-reference">
                    <TextInput
                      id="pf-waste-reference"
                      value={evidenceRef}
                      placeholder="Optional"
                      onChange={(_event, value) => setEvidenceRef(String(value))}
                      aria-label="Waste or loss reference"
                    />
                  </FormGroup>
                </div>
                <FormGroup label="Short reason" fieldId="pf-waste-reason">
                  <TextArea
                    id="pf-waste-reason"
                    value={reason}
                    rows={3}
                    onChange={(_event, value) => setReason(String(value))}
                    aria-label="Waste or loss reason"
                  />
                </FormGroup>
                {selectedLot && (
                  <div className="pf-toj-message">
                    <strong>{selectedLot.product_name || "Selected item"}</strong>
                    <span>{formatQuantity(selectedLot.qty_on_hand)} {selectedLot.uom || "units"} available at {selectedLot.location_code || "this location"}.</span>
                  </div>
                )}
                <div className="pf-toj-form-actions">
                  <Button variant="primary" onClick={() => void submitWaste()} isLoading={posting} isDisabled={posting}>
                    Save waste / loss
                  </Button>
                  <Button variant="secondary" onClick={() => void loadData()} isDisabled={posting}>
                    Refresh
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
          <Card className="pf-toj-work-card">
            <CardBody>
              <h2>Recent records</h2>
              <div className="pf-toj-table-wrap pf-toj-waste-table-wrap">
                <Table aria-label="Recent waste and loss records" variant="compact">
                  <Thead>
                    <Tr>
                      <Th>Record</Th>
                      <Th>Item</Th>
                      <Th>Location</Th>
                      <Th>Type</Th>
                      <Th textCenter>Quantity</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {events.slice(0, 80).map(event => (
                      <Tr key={event.id || event.exception_number}>
                        <Td dataLabel="Record">
                          <strong>{event.exception_number || "Recorded"}</strong>
                          <small>{compactDateTime(event.created_at)}</small>
                        </Td>
                        <Td dataLabel="Item">
                          <strong>{event.product_name || "Inventory item"}</strong>
                          <small>{event.sku || ""}</small>
                        </Td>
                        <Td dataLabel="Location">{[event.location_code, event.location_name].filter(Boolean).join(" - ")}</Td>
                        <Td dataLabel="Type">{eventTypeLabel(event.exception_type)}</Td>
                        <Td dataLabel="Quantity" textCenter>{formatQuantity(event.quantity)}</Td>
                        <Td dataLabel="Status">{event.status || "Recorded"}</Td>
                      </Tr>
                    ))}
                    {!events.length && !loading && (
                      <Tr>
                        <Td colSpan={6}>
                          <div className="pf-toj-empty">
                            <strong>No waste or loss records are visible.</strong>
                            <span>Saved records for this role will appear here.</span>
                          </div>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageSection>
  );
}
